import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  ChevronLeft, Heart, Crown, Star, Sparkles, Check, Clock, 
  Users, MessageCircle, Award, ArrowRight, Phone, Loader2, CreditCard, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

// 사용자 현지 통화 자동 감지 (KRW/USD/CAD)
function detectUserCurrency() {
  try {
    const pref = localStorage.getItem("currency_pref");
    if (pref && ["KRW", "USD", "CAD"].includes(pref)) return pref;
  } catch {}
  const lang = (typeof navigator !== "undefined" && (navigator.language || navigator.userLanguage) || "").toLowerCase();
  if (lang.startsWith("ko")) return "KRW";
  if (lang === "en-ca" || lang === "fr-ca") return "CAD";
  try {
    const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone || "").toLowerCase();
    if (tz.includes("seoul")) return "KRW";
    if (/(toronto|vancouver|montreal|edmonton|halifax|winnipeg|canada)/.test(tz)) return "CAD";
  } catch {}
  return "USD";
}

const API_URL = process.env.REACT_APP_BACKEND_URL;

const plans = [
  {
    id: "elite",
    name: "엘리트",
    nameEn: "Elite",
    price: 500,
    color: "from-stone-100 to-stone-200",
    textColor: "text-stone-800",
    accentColor: "text-stone-500",
    borderColor: "border-stone-300",
    bgColor: "bg-stone-100/30",
    icon: Star,
    duration: "6개월",
    interview: "커플매니저와 30분간 인터뷰",
    matching: "1:1 맞춤소개팅 3회",
    matchingType: "일반매칭",
    extras: [],
    popular: false,
  },
  {
    id: "highend",
    name: "하이앤드",
    nameEn: "High-End",
    price: 1000,
    color: "from-stone-200 to-stone-300",
    textColor: "text-stone-800",
    accentColor: "text-stone-600",
    borderColor: "border-stone-300",
    bgColor: "bg-stone-200/30",
    icon: Award,
    duration: "6개월",
    interview: "커플매니저와 30분간 인터뷰",
    matching: "1:1 맞춤소개팅 3회 +α",
    matchingType: "고급매칭",
    extras: [],
    popular: false,
  },
  {
    id: "noble",
    name: "노블",
    nameEn: "Noble",
    price: 2000,
    color: "from-stone-300 to-stone-400",
    textColor: "text-stone-800",
    accentColor: "text-stone-600",
    borderColor: "border-stone-400",
    bgColor: "bg-stone-300/30",
    icon: Sparkles,
    duration: "1년",
    interview: "리즈와 30분간 인터뷰",
    matching: "1:1 맞춤소개팅 3회 +α",
    matchingType: "고급매칭",
    extras: ["연애코칭, 기술, 컨설팅"],
    popular: true,
  },
  {
    id: "royal",
    name: "로얄",
    nameEn: "Royal",
    price: 5000,
    color: "from-stone-400 to-stone-500",
    textColor: "text-white",
    accentColor: "text-stone-200",
    borderColor: "border-stone-500",
    bgColor: "bg-stone-400/30",
    icon: Crown,
    duration: "1년",
    interview: "리즈와 1시간 인터뷰",
    matching: "수석 VIP 멤버를 위한 맞춤매칭 무제한",
    matchingType: "최고급 매칭",
    extras: ["연애코칭, 기술, 컨설팅 무제한", "힐링캠프 참여 무료"],
    popular: false,
  },
];

const processSteps = [
  { step: 1, title: "계약서 작성", description: "서비스 이용 계약 체결" },
  { step: 2, title: "자기소개서 작성", description: "상세한 프로필 작성" },
  { step: 3, title: "혼인관계증명서 제출", description: "신원 확인 서류 제출" },
  { step: 4, title: "커플매니저 배정", description: "전담 매니저 배정" },
  { step: 5, title: "줌 인터뷰", description: "1:1 화상 인터뷰 진행" },
  { step: 6, title: "소개팅 진행", description: "맞춤 매칭 시작" },
];

export const DatingAgencyPage = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { user, token } = useAuth();
  // 결제 모달 별도 상태
  const [payPlan, setPayPlan] = useState(null);
  const [payForm, setPayForm] = useState({ name: "", phone: "", message: "" });
  const [paying, setPaying] = useState(false);

  const handleOpenPayment = (plan) => {
    if (!user || !token) {
      toast.info("결제를 진행하려면 먼저 로그인 또는 회원가입해주세요.");
      window.dispatchEvent(
        new CustomEvent("openAuthModal", { detail: { mode: "login" } })
      );
      return;
    }
    setPayPlan(plan);
    setPayForm({
      name: user.username || "",
      phone: user.phone || "",
      message: "",
    });
  };

  const closePaymentModal = () => {
    setPayPlan(null);
    setPayForm({ name: "", phone: "", message: "" });
  };

  const handleStartPayment = async () => {
    const name = payForm.name.trim();
    const phone = payForm.phone.trim();
    if (!name) { toast.error("이름을 입력해주세요."); return; }
    if (!phone || phone.length < 4) { toast.error("연락처를 입력해주세요."); return; }

    setPaying(true);
    try {
      // 1) 상담 신청 정보 먼저 저장 (관리자 알림용 — 결제 전에 contact 정보 확보)
      try {
        await fetch(`${API_URL}/api/dating-agency/inquiries`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            plan_id: payPlan.id,
            plan_name: payPlan.name,
            plan_price: payPlan.price,
            name,
            phone,
            email: user?.email || "",
            message: payForm.message?.trim() || "(결제 진행 중)",
          }),
        });
      } catch { /* 알림 실패해도 결제는 진행 */ }

      // 2) PayPal 주문 생성 → approve_url 로 리다이렉트
      // 사용자 현지 통화 자동 감지 (KRW/USD/CAD)
      const localCur = detectUserCurrency();
      const res = await fetch(`${API_URL}/api/paypal/orders/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          plan_id: `matching_${payPlan.id}`,
          return_path: "/payment/success",
          cancel_path: "/payment/cancel",
          display_currency: localCur,
          metadata: { name, phone, message: payForm.message?.trim() || "" },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.approve_url) {
        const msg = data?.detail || data?.message || "결제 페이지로 이동할 수 없습니다.";
        throw new Error(msg);
      }
      // PayPal 결제 페이지로 이동
      window.location.href = data.approve_url;
    } catch (e) {
      toast.error(e.message || "결제 처리 중 오류가 발생했습니다.");
      setPaying(false);
    }
  };

  const resetForm = () => setForm({ name: "", phone: "", email: "", message: "" });

  const closeModal = () => {
    setSelectedPlan(null);
    resetForm();
  };

  const handleSubmitInquiry = async () => {
    const name = form.name.trim();
    const phone = form.phone.trim();
    const email = form.email.trim();
    if (!name) { toast.error("이름을 입력해주세요."); return; }
    if (!phone || phone.length < 4) { toast.error("연락처를 입력해주세요."); return; }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("올바른 이메일을 입력해주세요."); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/dating-agency/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_id: selectedPlan.id,
          plan_name: selectedPlan.name,
          plan_price: selectedPlan.price,
          plan_duration: selectedPlan.duration,
          name,
          phone,
          email,
          message: form.message?.trim() || "",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data?.detail || data?.message || "신청 처리 중 오류가 발생했습니다.";
        throw new Error(msg);
      }
      toast.success(data.message || "상담 신청이 정상적으로 접수되었습니다.", {
        description: "빠른 시일 내 커플매니저가 연락드릴 예정입니다.",
        duration: 5000,
      });
      closeModal();
    } catch (e) {
      toast.error(e.message || "신청 처리 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] pt-20">
      {/* Hero Section */}
      <div className="relative text-white py-16 lg:py-24 overflow-hidden min-h-[500px] flex items-center">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/images/dating-agency-bg.png')" }}
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/50 via-white/35 to-white/20" />
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-stone-700 hover:text-stone-900 mb-8 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            홈으로 돌아가기
          </Link>
          
          <div className="text-center">
            <span className="text-stone-600 font-medium text-sm tracking-wider uppercase mb-4 block">
              1:1 맞춤소개팅
            </span>
            <h1 className="font-sans text-4xl md:text-5xl lg:text-6xl font-[720] text-stone-900 mb-4">
              리즈 연애정보회사
            </h1>
            <p className="text-xl md:text-2xl text-stone-700 mb-6">
              완벽한 인생을 위한 마지막 퍼즐 한 조각,
            </p>
            <p className="text-xl md:text-2xl text-stone-800 font-medium">
              리즈가 찾아드립니다.
            </p>
          </div>
        </div>
      </div>

      {/* Process Section */}
      <div className="py-16 bg-gradient-to-br from-stone-50 via-stone-100 to-stone-50 relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-stone-300/40 to-stone-200/30 rounded-full blur-3xl -translate-x-1/4 -translate-y-1/4"></div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-stone-300/30 to-stone-200/20 rounded-full blur-3xl translate-x-1/4 translate-y-1/4"></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <h2 className="font-sans text-2xl md:text-3xl font-[720] text-stone-800 mb-4">
              진행 순서
            </h2>
            <p className="text-stone-600">
              체계적인 프로세스로 최적의 인연을 찾아드립니다
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {processSteps.map((item, index) => (
              <div 
                key={item.step}
                className="relative text-center p-4 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm hover:shadow-md transition-shadow border border-stone-100"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-stone-500 to-stone-700 text-white flex items-center justify-center mx-auto mb-3 font-bold shadow-md">
                  {item.step}
                </div>
                <h3 className="font-medium text-stone-800 text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-stone-500">{item.description}</p>
                {index < processSteps.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-4 h-4 text-burgundy-300" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="py-16 lg:py-24 bg-gradient-to-br from-stone-100 via-stone-50 to-stone-100 relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-gradient-to-bl from-stone-300/30 to-stone-200/20 rounded-full blur-3xl translate-x-1/4 -translate-y-1/4"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-stone-300/30 to-stone-200/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-stone-200/30 to-stone-100/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <h2 className="font-sans text-2xl md:text-3xl font-[720] text-stone-800 mb-4">
              멤버십 플랜
            </h2>
            <p className="text-stone-600">
              나에게 맞는 플랜을 선택하세요
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => {
              const IconComponent = plan.icon;
              return (
                <div
                  key={plan.id}
                  className={`relative bg-gradient-to-br ${plan.color} rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 shadow-lg animate-flip-card ${
                    plan.popular ? 'ring-2 ring-gold-400 ring-offset-2' : ''
                  }`}
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gold-400 text-stone-800 text-xs font-bold rounded-full shadow-md">
                      BEST
                    </div>
                  )}

                  {/* Header */}
                  <div className="text-center mb-6">
                    <div className={`w-14 h-14 rounded-full ${plan.bgColor} backdrop-blur-sm flex items-center justify-center mx-auto mb-3 border border-white/20`}>
                      <IconComponent className={`w-7 h-7 ${plan.accentColor || plan.textColor}`} />
                    </div>
                    <h3 className={`text-2xl font-bold ${plan.textColor}`}>{plan.name}</h3>
                    <p className={`text-sm ${plan.accentColor || plan.textColor} opacity-80`}>{plan.nameEn}</p>
                  </div>

                  {/* Price */}
                  <div className="text-center mb-6">
                    <div className={`text-3xl font-bold ${plan.textColor}`}>
                      USD ${plan.price.toLocaleString()}
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-2">
                      <Check className={`w-4 h-4 ${plan.accentColor || plan.textColor} mt-0.5 flex-shrink-0`} />
                      <span className={`text-sm ${plan.textColor} opacity-90`}>{plan.interview}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className={`w-4 h-4 ${plan.accentColor || plan.textColor} mt-0.5 flex-shrink-0`} />
                      <span className={`text-sm ${plan.textColor} opacity-90`}>{plan.matching}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className={`w-4 h-4 ${plan.accentColor || plan.textColor} mt-0.5 flex-shrink-0`} />
                      <span className={`text-sm ${plan.textColor} opacity-90`}>{plan.matchingType}</span>
                    </div>
                    {plan.extras.map((extra, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Check className={`w-4 h-4 ${plan.accentColor || plan.textColor} mt-0.5 flex-shrink-0`} />
                        <span className={`text-sm ${plan.textColor} opacity-90`}>{extra}</span>
                      </div>
                    ))}
                  </div>

                  {/* Duration */}
                  <div className={`flex items-center justify-center gap-2 mb-6 py-2 rounded-lg ${plan.bgColor} backdrop-blur-sm border border-white/10`}>
                    <Clock className={`w-4 h-4 ${plan.accentColor || plan.textColor}`} />
                    <span className={`text-sm font-medium ${plan.textColor}`}>{plan.duration}</span>
                  </div>

                  {/* CTA Buttons — 상담 신청 + 결제하기 */}
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-gold-400 hover:bg-gold-500 text-stone-800 font-semibold' : 'bg-white/90 hover:bg-white text-stone-700 font-medium'}`}
                    onClick={() => setSelectedPlan(plan)}
                    data-testid={`plan-cta-${plan.id}`}
                  >
                    상담 신청하기
                  </Button>
                  <Button
                    variant="outline"
                    className={`w-full mt-2 ${plan.popular ? 'border-gold-400/60 text-stone-800 hover:bg-gold-400/10' : 'border-white/40 text-stone-700 hover:bg-white/30'} font-semibold backdrop-blur-sm`}
                    onClick={() => handleOpenPayment(plan)}
                    data-testid={`plan-pay-${plan.id}`}
                  >
                    바로 결제하기 · USD ${plan.price.toLocaleString()}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="py-16 bg-gradient-to-br from-stone-100 via-rose-50 to-amber-50 relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-gradient-to-br from-stone-300/30 to-stone-200/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-gradient-to-tl from-stone-300/30 to-stone-200/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <h2 className="font-sans text-2xl md:text-3xl font-[720] text-stone-800 mb-4">
              플랜 비교
            </h2>
          </div>

          <div className="overflow-x-auto rounded-xl border border-stone-200 shadow-lg bg-white/80 backdrop-blur-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-stone-700 via-stone-800 to-stone-700 text-white">
                  <th className="text-left py-4 px-4 font-medium border-r border-stone-600">
                    <div>항목</div>
                    <div className="text-xs font-normal opacity-70">Item</div>
                  </th>
                  {plans.map((plan, index) => (
                    <th 
                      key={plan.id} 
                      className={`py-4 px-4 font-bold text-center ${index < plans.length - 1 ? 'border-r border-stone-600' : ''}`}
                    >
                      <div>{plan.name}</div>
                      <div className="text-xs font-normal opacity-70">{plan.nameEn}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="bg-stone-100">
                  <td className="py-4 px-4 text-stone-700 font-medium border-r border-stone-200">
                    <div>가격</div>
                    <div className="text-xs font-normal text-stone-500">Price</div>
                  </td>
                  {plans.map((plan, index) => (
                    <td 
                      key={plan.id} 
                      className={`py-4 px-4 text-center font-bold text-stone-800 ${index < plans.length - 1 ? 'border-r border-stone-200' : ''}`}
                    >
                      USD ${plan.price.toLocaleString()}
                    </td>
                  ))}
                </tr>
                <tr className="bg-stone-50">
                  <td className="py-4 px-4 text-stone-700 font-medium border-r border-stone-200">
                    <div>인터뷰</div>
                    <div className="text-xs font-normal text-stone-500">Interview</div>
                  </td>
                  {plans.map((plan, index) => (
                    <td 
                      key={plan.id} 
                      className={`py-4 px-4 text-center text-sm text-stone-700 ${index < plans.length - 1 ? 'border-r border-stone-200' : ''}`}
                    >
                      {plan.interview}
                    </td>
                  ))}
                </tr>
                <tr className="bg-stone-100">
                  <td className="py-4 px-4 text-stone-700 font-medium border-r border-stone-200">
                    <div>소개팅</div>
                    <div className="text-xs font-normal text-stone-500">Matchmaking</div>
                  </td>
                  {plans.map((plan, index) => (
                    <td 
                      key={plan.id} 
                      className={`py-4 px-4 text-center text-sm text-stone-700 ${index < plans.length - 1 ? 'border-r border-stone-200' : ''}`}
                    >
                      {plan.matching}
                    </td>
                  ))}
                </tr>
                <tr className="bg-stone-50">
                  <td className="py-4 px-4 text-stone-700 font-medium border-r border-stone-200">
                    <div>매칭 유형</div>
                    <div className="text-xs font-normal text-stone-500">Matching Type</div>
                  </td>
                  {plans.map((plan, index) => (
                    <td 
                      key={plan.id} 
                      className={`py-4 px-4 text-center text-sm text-stone-700 ${index < plans.length - 1 ? 'border-r border-stone-200' : ''}`}
                    >
                      {plan.matchingType}
                    </td>
                  ))}
                </tr>
                <tr className="bg-stone-100">
                  <td className="py-4 px-4 text-stone-700 font-medium border-r border-stone-200">
                    <div>추가 혜택</div>
                    <div className="text-xs font-normal text-stone-500">Benefits</div>
                  </td>
                  {plans.map((plan, index) => (
                    <td 
                      key={plan.id} 
                      className={`py-4 px-4 text-center text-sm text-stone-700 ${index < plans.length - 1 ? 'border-r border-stone-200' : ''}`}
                    >
                      {plan.extras.length > 0 ? plan.extras.join(', ') : '-'}
                    </td>
                  ))}
                </tr>
                <tr className="bg-stone-50">
                  <td className="py-4 px-4 text-stone-700 font-medium border-r border-stone-200">
                    <div>기간</div>
                    <div className="text-xs font-normal text-stone-500">Duration</div>
                  </td>
                  {plans.map((plan, index) => (
                    <td 
                      key={plan.id} 
                      className={`py-4 px-4 text-center font-medium text-stone-800 ${index < plans.length - 1 ? 'border-r border-stone-200' : ''}`}
                    >
                      {plan.duration}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="py-16 bg-gradient-to-br from-stone-200 via-rose-100 via-50% to-amber-100 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-25">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gold-400 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-rose-300 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-rose-200 rounded-full blur-3xl opacity-40"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <Heart className="w-12 h-12 mx-auto mb-6 text-burgundy-400" />
          <h2 className="font-sans text-2xl md:text-3xl font-[720] mb-4 text-stone-800">
            당신의 인연을 찾아드립니다
          </h2>
          <p className="text-stone-600 mb-8">
            전문 커플매니저가 1:1로 맞춤 상담을 진행해드립니다.<br />
            지금 바로 상담을 신청하세요.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://pf.kakao.com/_liztv/chat"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                size="lg" 
                className="bg-[#FEE500] hover:bg-[#FDD835] text-stone-800 font-medium"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                카카오톡 상담
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Consultation Modal */}
      {selectedPlan && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div 
            className="bg-white rounded-2xl max-w-md w-full p-6 animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${selectedPlan.color} flex items-center justify-center mx-auto mb-4`}>
                <selectedPlan.icon className={`w-8 h-8 ${selectedPlan.textColor}`} />
              </div>
              <h3 className="text-xl font-bold text-stone-800 mb-2">
                {selectedPlan.name} 플랜 상담 신청
              </h3>
              <p className="text-stone-600">
                USD ${selectedPlan.price.toLocaleString()} / {selectedPlan.duration}
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <input 
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="이름"
                disabled={submitting}
                className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-500"
                data-testid="dating-agency-input-name"
              />
              <input 
                type="tel"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="연락처"
                disabled={submitting}
                className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-500"
                data-testid="dating-agency-input-phone"
              />
              <input 
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="이메일"
                disabled={submitting}
                className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-500"
                data-testid="dating-agency-input-email"
              />
              <textarea
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                placeholder="문의 내용 (선택사항)"
                rows={3}
                disabled={submitting}
                className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-500 resize-none"
                data-testid="dating-agency-input-message"
              />
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline"
                className="flex-1"
                onClick={closeModal}
                disabled={submitting}
              >
                취소
              </Button>
              <Button 
                className="flex-1 btn-primary"
                onClick={handleSubmitInquiry}
                disabled={submitting}
                data-testid="dating-agency-submit"
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>처리 중…</span>
                ) : (
                  "신청하기"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 결제 모달 — 바로 결제하기 */}
      {payPlan && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closePaymentModal}
          data-testid="payment-modal"
        >
          <div 
            className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closePaymentModal}
              className="absolute top-4 right-4 text-stone-400 hover:text-stone-700"
              aria-label="닫기"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-burgundy-100 to-rose-100 mb-3">
                <CreditCard className="w-7 h-7 text-burgundy-600" />
              </div>
              <h3 className="text-2xl font-bold text-stone-800 mb-1">{payPlan.name} 플랜 결제</h3>
              <div className="text-stone-500 text-sm">
                <span className="font-semibold text-burgundy-600 text-lg">USD ${payPlan.price.toLocaleString()}</span>
                <span className="ml-2">/ {payPlan.duration}</span>
              </div>
            </div>

            {/* 안내 박스 */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-5 text-xs text-amber-800 leading-relaxed">
              결제 완료 후 6단계 프로세스가 시작됩니다.<br/>
              <span className="text-amber-600">계약서 작성 → 자기소개서 → 혼인관계증명서 → 커플매니저 배정 → 줌 인터뷰 → 소개팅</span>
            </div>

            <div className="space-y-3 mb-6">
              <input
                type="text"
                value={payForm.name}
                onChange={(e) => setPayForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="이름 (실명)"
                disabled={paying}
                className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-500"
                data-testid="payment-input-name"
              />
              <input
                type="tel"
                value={payForm.phone}
                onChange={(e) => setPayForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="연락처"
                disabled={paying}
                className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-500"
                data-testid="payment-input-phone"
              />
              <textarea
                value={payForm.message}
                onChange={(e) => setPayForm((f) => ({ ...f, message: e.target.value }))}
                placeholder="요청사항 (선택)"
                rows={2}
                disabled={paying}
                className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-500 resize-none"
                data-testid="payment-input-message"
              />
              <div className="text-xs text-stone-500 pt-1">
                결제 이메일: <span className="font-medium text-stone-700">{user?.email}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={closePaymentModal} disabled={paying}>
                취소
              </Button>
              <Button
                className="flex-1 bg-burgundy-600 hover:bg-burgundy-700 text-white font-semibold"
                onClick={handleStartPayment}
                disabled={paying}
                data-testid="payment-submit"
              >
                {paying ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />PayPal로 이동…</span>
                ) : (
                  <span className="inline-flex items-center gap-2"><CreditCard className="w-4 h-4" />USD ${payPlan.price.toLocaleString()} 결제</span>
                )}
              </Button>
            </div>
            <p className="text-[11px] text-stone-400 text-center mt-3">PayPal로 안전하게 결제됩니다 · 결제 정보는 저장되지 않습니다</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatingAgencyPage;
